import { useMemo, useRef, useState } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, Upload, Check, AlertCircle } from 'lucide-react';
import GoldRule from './GoldRule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Splits a CSV/TSV line respecting double quotes.
const splitLine = (line, sep) => {
  const out = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((v) => v.trim());
};

const FIELD_ALIASES = {
  code: 'code',
  sku: 'code',
  name: 'name',
  title: 'name',
  category: 'category',
  type: 'category',
  dimensions: 'dimensions',
  size: 'dimensions',
  leather: 'leather',
  material: 'leather',
  description: 'description',
  features: 'features',
  colors: 'colors',
  colours: 'colors',
  sets: 'set_slugs',
  set_slugs: 'set_slugs',
  price: 'price',
  currency: 'currency',
};

const parseSheet = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], headers: [], error: 'Paste a header row plus at least one product.' };
  const sep = lines[0].includes('\t') ? '\t' : ',';
  const headers = splitLine(lines[0], sep).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  const mapped = headers.map((h) => FIELD_ALIASES[h] || null);
  if (!mapped.includes('code')) {
    return { rows: [], headers, error: 'No "code" column found — add a column headed code (or sku).' };
  }
  const rows = lines.slice(1).map((line) => {
    const cells = splitLine(line, sep);
    const row = {};
    mapped.forEach((field, i) => {
      if (!field) return;
      const v = cells[i] ?? '';
      if (field === 'features' || field === 'set_slugs') {
        row[field] = v ? v.split(/[;|]/).map((s) => s.trim()).filter(Boolean) : [];
      } else if (field === 'colors') {
        row[field] = v
          ? v
              .split(/[;|]/)
              .map((chunk) => {
                const [label, hex, ref] = chunk.split(':').map((s) => (s || '').trim());
                return label ? { label, hex: hex || '#1D2124', ref: ref || null } : null;
              })
              .filter(Boolean)
          : [];
      } else if (field === 'price') {
        const n = parseFloat(v.replace(/[^0-9.]/g, ''));
        row[field] = Number.isFinite(n) ? n : null;
      } else {
        row[field] = v;
      }
    });
    return row;
  });
  return { rows: rows.filter((r) => r.code), headers, error: null };
};

export const ImportPanel = ({ onChanged }) => {
  const fileRef = useRef(null);
  const [picked, setPicked] = useState([]);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgResult, setImgResult] = useState(null);

  const [sheet, setSheet] = useState('');
  const [rowBusy, setRowBusy] = useState(false);
  const [rowResult, setRowResult] = useState(null);

  const parsed = useMemo(() => (sheet.trim() ? parseSheet(sheet) : null), [sheet]);

  const uploadImages = async () => {
    if (!picked.length) return;
    setImgBusy(true);
    setImgResult(null);
    const CHUNK = 8;
    const agg = { stored: 0, failed: 0, codes: [], codes_without_product: [], results: [] };
    try {
      for (let i = 0; i < picked.length; i += CHUNK) {
        const fd = new FormData();
        picked.slice(i, i + CHUNK).forEach((f) => fd.append('files', f));
        const { data } = await adminAxios.post(`${API}/admin/images/bulk`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        agg.stored += data.stored;
        agg.failed += data.failed;
        agg.codes = [...new Set([...agg.codes, ...data.codes])];
        agg.codes_without_product = [
          ...new Set([...agg.codes_without_product, ...data.codes_without_product]),
        ];
        agg.results = [...agg.results, ...data.results];
        setImgResult({ ...agg, partial: i + CHUNK < picked.length });
      }
      setImgResult({ ...agg, partial: false });
      setPicked([]);
      if (fileRef.current) fileRef.current.value = '';
      onChanged?.();
    } catch (e) {
      setImgResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setImgBusy(false);
    }
  };

  const importRows = async () => {
    if (!parsed?.rows?.length) return;
    setRowBusy(true);
    setRowResult(null);
    try {
      const { data } = await adminAxios.post(`${API}/admin/products/bulk`, {
        rows: parsed.rows,
        overwrite: true,
      });
      setRowResult(data);
      onChanged?.();
    } catch (e) {
      setRowResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setRowBusy(false);
    }
  };

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20"
      data-testid="admin-import-panel"
    >
      <div className="hairline border-gold bg-mb-surface p-8 md:p-10">
        <div className="eyebrow text-gold mb-5">Bulk import</div>
        <h2 className="font-display text-3xl text-mb">Bring in a whole range at once.</h2>
        <div className="mt-6">
          <GoldRule />
        </div>

        {/* 1 — photographs */}
        <div className="mt-10 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow-sm text-mb-muted mb-4">01 · Photographs</div>
            <p className="text-[13px] text-mb-muted leading-relaxed">
              Select any number of files. The code is read from the filename —
              <span className="text-mb"> gv-4439.jpg</span> becomes GV 4439,
              <span className="text-mb"> gv-4439-2.jpg</span> becomes a second
              gallery angle, and <span className="text-mb">x-pen-01.jpg</span>{' '}
              works too. Everything is converted to JPEG and mirrored to storage.
            </p>

            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*"
              data-testid="import-files-input"
              onChange={(e) => {
                setPicked(Array.from(e.target.files || []));
                setImgResult(null);
              }}
              className="mt-6 block w-full text-[12px] text-mb-muted file:mr-4 file:py-3 file:px-5 file:border-[0.5px] file:border-[var(--mb-gold)] file:bg-transparent file:text-[var(--mb-gold)] file:font-display file:text-[11px] file:tracking-[0.2em] file:uppercase file:cursor-pointer"
            />

            {picked.length > 0 && (
              <div className="mt-5 eyebrow-sm text-gold" data-testid="import-files-count">
                {picked.length} file{picked.length === 1 ? '' : 's'} ready
              </div>
            )}

            <button
              type="button"
              onClick={uploadImages}
              disabled={!picked.length || imgBusy}
              data-testid="import-upload-btn"
              className={`mt-6 btn-gold ${!picked.length || imgBusy ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {imgBusy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Uploading
                </>
              ) : (
                <>
                  <Upload size={14} strokeWidth={1.5} /> Upload photographs
                </>
              )}
            </button>

            {imgResult && (
              <div className="mt-6 hairline border-subtle p-5" data-testid="import-image-result">
                {imgResult.error ? (
                  <div className="flex gap-3 text-[13px] text-mb">
                    <AlertCircle size={15} className="text-gold shrink-0 mt-[2px]" />
                    {imgResult.error}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 eyebrow-sm text-gold">
                      <Check size={13} /> {imgResult.stored} stored
                      {imgResult.failed ? ` · ${imgResult.failed} failed` : ''}
                      {imgResult.partial ? ' · still going' : ''}
                    </div>
                    {imgResult.codes?.length > 0 && (
                      <div className="mt-4 text-[12px] text-mb-muted leading-relaxed">
                        Codes seen: {imgResult.codes.join(', ')}
                      </div>
                    )}
                    {imgResult.codes_without_product?.length > 0 && (
                      <div className="mt-4 text-[12px] text-mb">
                        No product yet for:{' '}
                        <span className="text-gold">
                          {imgResult.codes_without_product.join(', ')}
                        </span>{' '}
                        — import the catalogue rows below and the photos will
                        attach themselves.
                      </div>
                    )}
                    {imgResult.results
                      ?.filter((r) => !r.ok)
                      .slice(0, 8)
                      .map((r) => (
                        <div key={r.filename} className="mt-3 text-[12px] text-mb-muted">
                          {r.filename}: {r.error}
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 2 — catalogue rows */}
          <div>
            <div className="eyebrow-sm text-mb-muted mb-4">02 · Catalogue data</div>
            <p className="text-[13px] text-mb-muted leading-relaxed">
              Paste straight from a spreadsheet (tab or comma separated). First
              row is the header. Recognised columns:{' '}
              <span className="text-mb">
                code, name, category, dimensions, leather, description, features,
                colors, sets, price, currency
              </span>
              . Separate multiple features/sets with <span className="text-mb">;</span>{' '}
              and write colours as{' '}
              <span className="text-mb">Black:#111111:GV 4943-6</span>.
            </p>

            <textarea
              value={sheet}
              onChange={(e) => {
                setSheet(e.target.value);
                setRowResult(null);
              }}
              rows={9}
              data-testid="import-sheet-input"
              placeholder={'code\tname\tcategory\tdimensions\tleather\tprice'}
              className="mb-input mt-6 font-mono text-[12px]"
            />

            {parsed?.error && (
              <div className="mt-4 text-[12px] text-gold" data-testid="import-parse-error">
                {parsed.error}
              </div>
            )}
            {parsed && !parsed.error && (
              <div className="mt-4 eyebrow-sm text-gold" data-testid="import-parse-count">
                {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} ready
              </div>
            )}

            <button
              type="button"
              onClick={importRows}
              disabled={!parsed?.rows?.length || rowBusy}
              data-testid="import-rows-btn"
              className={`mt-6 btn-outline-gold ${
                !parsed?.rows?.length || rowBusy ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            >
              {rowBusy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Importing
                </>
              ) : (
                'Import catalogue rows'
              )}
            </button>

            {rowResult && (
              <div className="mt-6 hairline border-subtle p-5" data-testid="import-rows-result">
                {rowResult.error ? (
                  <div className="flex gap-3 text-[13px] text-mb">
                    <AlertCircle size={15} className="text-gold shrink-0 mt-[2px]" />
                    {rowResult.error}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 eyebrow-sm text-gold">
                      <Check size={13} /> {rowResult.created?.length || 0} created ·{' '}
                      {rowResult.updated?.length || 0} updated
                      {rowResult.errors?.length ? ` · ${rowResult.errors.length} rejected` : ''}
                    </div>
                    {rowResult.errors?.slice(0, 10).map((e, i) => (
                      <div key={`${e.code}-${i}`} className="mt-3 text-[12px] text-mb-muted">
                        {e.code}: {e.error}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImportPanel;
